import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ClipboardEvent } from "react";
import { useNavigate } from "react-router";
import { api, errorText, newRequestId, type AssetDto, type InputAcceptedDto } from "../api/client";
import { Recorder } from "./Recorder";
import { rejectText, uploadAsset } from "./uploads";

type Attachment = {
  id: string;
  file: Blob;
  name: string;
  preview?: string;
  progress: number;
  asset?: AssetDto;
  error?: string;
};

/**
 * Gemeinsame Erfassung: Freitext (inkl. Tastatur-Diktat), Push-to-talk, Kamera, Dateiauswahl, mehrere Bilder,
 * Einfügen aus der Zwischenablage. Alles landet als ein Eingang im selben Verarbeitungsweg.
 */
export function CaptureBox({ compact = false }: { compact?: boolean }) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [requestId, setRequestId] = useState(() => newRequestId("input"));
  const [error, setError] = useState<string | null>(null);
  const camera = useRef<HTMLInputElement>(null);
  const files = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const previews = useRef<string[]>([]);
  useEffect(() => () => previews.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const update = (id: string, patch: Partial<Attachment>) => setAttachments((list) => list.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const add = (blobs: Blob[], names?: string[]) => {
    const next = blobs.slice(0, 10 - attachments.length).map((file, i) => ({
      id: crypto.randomUUID(),
      file,
      name: names?.[i] ?? (file instanceof File ? file.name : `aufnahme.${file.type.split("/")[1] ?? "bin"}`),
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
      progress: 0,
    }));
    next.forEach((a) => a.preview && previews.current.push(a.preview));
    setAttachments((list) => [...list, ...next]);
    for (const a of next) {
      uploadAsset(a.file, a.name, (p) => update(a.id, { progress: p }))
        .then((asset) => update(a.id, { asset, progress: 1, error: asset.status === "rejected" ? rejectText(asset.rejectCode) : undefined }))
        .catch((e) => update(a.id, { error: e instanceof Error ? errorText(e) || e.message : "Upload fehlgeschlagen." }));
    }
  };

  const onPaste = (e: ClipboardEvent) => {
    const images = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith("image/"));
    if (images.length > 0) {
      e.preventDefault();
      add(images);
    }
  };

  const ready = attachments.every((a) => a.asset?.status === "available");
  const submit = useMutation({
    mutationFn: () =>
      api<InputAcceptedDto>("/api/v1/inputs", {
        body: {
          schemaVersion: "1.0",
          clientRequestId: requestId,
          text: text.trim() || null,
          assetIds: attachments.map((a) => a.asset!.id),
          clientCreatedAt: new Date().toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale: navigator.language,
        },
      }),
    onSuccess: async (res) => {
      setText("");
      setAttachments([]);
      setRequestId(newRequestId("input"));
      setError(null);
      await qc.invalidateQueries({ queryKey: ["inputs"] });
      navigate(`/inbox#${res.inputId}`);
    },
    onError: (e) => setError(errorText(e)),
  });

  const canSubmit = (text.trim().length > 0 || attachments.length > 0) && ready && !submit.isPending;

  return (
    <section className={`card capture${compact ? " compact" : ""}`} aria-label="Neuer Eintrag">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onPaste={onPaste}
        rows={compact && !text ? 1 : 3}
        maxLength={8000}
        placeholder={compact ? "Termin oder Einkauf eintippen …" : "z. B. „Morgen 15 Uhr Zahnarzt Greta“ oder „2 Packungen Nudeln“"}
        aria-label="Text"
      />
      {attachments.length > 0 && (
        <ul className="attachments">
          {attachments.map((a) => (
            <li key={a.id} className={a.error ? "failed" : ""}>
              {a.preview ? <img src={a.preview} alt="" /> : <span className="file-icon" aria-hidden="true">🎙️</span>}
              <div className="meta">
                <span className="name">{a.name}</span>
                {a.error ? (
                  <span className="error small">{a.error}</span>
                ) : a.asset?.status === "available" ? (
                  <span className="ok small">bereit</span>
                ) : (
                  <progress value={a.progress} max={1} aria-label="Upload-Fortschritt" />
                )}
              </div>
              <button type="button" className="btn ghost" aria-label="Entfernen" onClick={() => setAttachments((l) => l.filter((x) => x.id !== a.id))}>
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="capture-actions">
        {/* Kompakt (Heute/Eingang): Mikrofon steckt in der Tableiste. */}
        {!compact && <Recorder onRecorded={(blob) => add([blob])} disabled={submit.isPending} />}
        <button type="button" className="btn icon" onClick={() => camera.current?.click()} aria-label="Foto aufnehmen" title="Kamera">
          📷 <span className="label">Foto</span>
        </button>
        <button type="button" className="btn icon" onClick={() => files.current?.click()} aria-label="Bilder auswählen" title="Bilder/Screenshots">
          🖼️ <span className="label">Bilder</span>
        </button>
        <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={(e) => add(Array.from(e.target.files ?? []))} />
        <input ref={files} type="file" accept="image/*,audio/*" multiple hidden onChange={(e) => add(Array.from(e.target.files ?? []))} />
        <button type="button" className="btn primary grow" disabled={!canSubmit} onClick={() => submit.mutate()}>
          {submit.isPending ? "Wird übergeben …" : "Übergeben"}
        </button>
      </div>
      {!ready && attachments.length > 0 && !attachments.some((a) => a.error) && <p className="hint small">Dateien werden hochgeladen …</p>}
      {error && <p className="error">{error}</p>}
    </section>
  );
}
